import { CreatePrescriptionOrderDto } from './../proxy/skybound/sky-gen1/prescriptions/models';
import { Injectable, signal } from '@angular/core';
import { PharmacyService } from '../proxy/skybound/sky-gen1/pharmacy';
import { LocationDto, LocationService } from '../proxy/skybound/sky-gen1/location';
import { environment } from 'src/environments/environment';
import { CacheService } from './cache.service';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { PharmacyProfile } from '../models/pharmacy-profile.model';
import { PharmacyLinkService } from '../proxy/skybound/sky-gen1/pharmacy-link';
import { NavController } from '@ionic/angular';
import { StorageService } from './storage.service';
import { AppConstants } from 'src/app/app.constants';
import { PatientService } from '../proxy/skybound/sky-gen1/patient';
import { PrescriptionDto, PrescriptionService } from '../proxy/skybound/sky-gen1/prescriptions';
import { PrescriptionState, PrescriptionOrderDeliveryType } from '../proxy/skybound/sky-gen1/pharmacy/prescription';
import { ListResultDto } from '@abp/ng.core';
import { PharmaciesFacadeService } from './pharmacies-facade.service';
import { CreateSaleOrderDto, SaleOrderDto, SaleOrderStatus } from '../proxy/skybound/sky-gen1/sale-order';
import { CreateSaleOrderItemDto } from '../proxy/skybound/sky-gen1/sale-order-item';
import { TaxStatus } from '../proxy/skybound/sky-gen1/common';
import { getLocalIsoTimestamp } from 'local-iso-timestamp';

@Injectable({
	providedIn: 'root'
})
export class PharmacyFacadeService
{
	public currentPharmacyProfile$ = new BehaviorSubject<PharmacyProfile | null>(null);

	constructor(
		private pharmacyLinkService: PharmacyLinkService,
		private locationService: LocationService,
		private cacheService: CacheService,
		private navController: NavController,
		private storageService: StorageService,
		private scriptService: PatientService,
		private prescriptionService: PrescriptionService,
		private patientService: PatientService,
		private pharmaciesFacadeService: PharmaciesFacadeService
	) { }

	public setCurrentPharmacyProfile(profile: PharmacyProfile | null, redirect = true): void
	{
		
		if (profile?.location)
		{
			this.cacheLocation(profile.location);
		}

		console.debug('Setting current pharmacy profile:', profile);

		this.currentPharmacyProfile$.next(profile);
		this.storageService.setItem(AppConstants.storage_lastPharmacyLinkProfile, profile);

		if (!profile && redirect)
		{
			//redirect to connect
			console.error('No profile provided, redirecting to connected pharmacies page.');
			this.navController.navigateRoot(['/account/connected']);
		}
	}

	public async loadCurrentPharmacyProfile(pharmacyLinkId: any, refreshCache: boolean = false): Promise<PharmacyProfile | null>
	{
		try
		{
			if (!pharmacyLinkId)
			{
				this.setCurrentPharmacyProfile(null);
				throw new Error('Pharmacy link ID is required');
				return null;
			}

			//get the pharmacy links and filter by pharmacyLinkId
			const pharmacyLinks = await firstValueFrom(this.pharmacyLinkService.getList({}));
			if (!pharmacyLinks || !pharmacyLinks.items || pharmacyLinks.items.length === 0)
			{
				this.setCurrentPharmacyProfile(null);
				throw new Error('No pharmacy links found');
				return null;
			}

			const pharmacyLink = pharmacyLinks.items.find(link => link.id === pharmacyLinkId);
			if (!pharmacyLink || !pharmacyLink.organizationUnitId || !pharmacyLink.id)
			{
				this.setCurrentPharmacyProfile(null);
				throw new Error('Pharmacy not found');
				return null;
			}

			const locations = await firstValueFrom(this.pharmaciesFacadeService.getLocationsCached(refreshCache));
			if (!locations || !locations.items || locations.items.length === 0)
			{
				this.setCurrentPharmacyProfile(null);
				throw new Error('No locations found');
				return null;
			}

			const location = locations.items.find(loc => loc.organizationUnitId === pharmacyLink.organizationUnitId);
			// If location is not found, throw an error
			if (!location)
			{
				this.setCurrentPharmacyProfile(null);
				throw new Error('Location not found');
				return null;
			}

			const profile = new PharmacyProfile(
				location,
				pharmacyLink
			);

			console.log('Loaded pharmacy profile:', profile);

			this.setCurrentPharmacyProfile(profile);

			return profile;
		}
		catch (error)
		{
			this.setCurrentPharmacyProfile(null);
			throw new Error(`Error loading pharmacy profile: ${error}`);
		}
	}

	public cacheLocation(location: LocationDto)
	{
		const apiUrl = environment.oAuthConfig?.issuer ?? '-';
		const cacheMilliseconds = 1 * 60 * 60000 * 2; //2 hours
		const cacheKey = `${apiUrl}-location-${location.id}`;
		this.cacheService.set(cacheKey, location, cacheMilliseconds);
	}

	public getLocationCached(id: string, refreshCache: boolean = false): Observable<LocationDto | null>
	{
		const apiUrl = environment.oAuthConfig?.issuer ?? '-';
		const cacheMilliseconds = 1 * 60 * 60000 * 2; //2 hours
		const cacheKey = `${apiUrl}-location-${id}`;
		if (refreshCache)
		{
			this.cacheService.clear(cacheKey);
		}

		return this.cacheService.get(cacheKey, this.getLocation(id), cacheMilliseconds);
	}

	private getLocation(id: string): Observable<LocationDto | null>
	{
		return this.locationService.get(id, {})
	}

	public getScriptsCached(pharmacyLinkId: string, dateFrom: string, dateTo: string, skipCount: number, maxResultCount: number = 10, refreshCache: boolean): Observable<ListResultDto<PrescriptionDto>>
	{
		if (!pharmacyLinkId)
		{
			throw new Error('Profile id is required');
		}
		const apiUrl = environment.oAuthConfig?.issuer ?? '-';
		const cacheMilliseconds = 1 * 60 * 60000 * 2; //2 hours
		const cacheKey = `${apiUrl}-scripts-${pharmacyLinkId}-${dateFrom}-${dateTo}-${skipCount}-${maxResultCount}`;
		if (refreshCache)
		{
			this.cacheService.clear(cacheKey);
		}

		return this.cacheService.get(cacheKey, this.getScripts(pharmacyLinkId, dateFrom, dateTo, skipCount, maxResultCount), cacheMilliseconds);

	}

	public getScripts(pharmacyLinkId: string, dateFrom: string, dateTo: string, skipCount: number, maxResultCount: number = 10): Observable<ListResultDto<PrescriptionDto>>
	{
		if (!dateFrom || !dateTo || !maxResultCount)
		{
			throw new Error('Missing required parameters');
		}

		const params =
		{
			pharmacyLinkId,
			dateFrom,
			dateTo,
			skipCount,
			maxResultCount
		};

		return this.prescriptionService.get(params, null);
	}

	/**
	 * Filters scripts to get those that are Active and have repeats due now or in the past
	 */
	public getScriptsDue(scripts: PrescriptionDto[]): PrescriptionDtoExt[] 
	{
		const scriptItems = this.setScriptRepeatStatuses(scripts);

		if (!scriptItems || scriptItems.length === 0) {
			return [];
		}
		return scriptItems.filter(s =>
			s.state === 'Active' &&
			(s.repeatStatus === 'Order now')
		);
	}

	/**
	 * Filters scripts to get those that are Active 
	 */
	public getScriptsAvailable(scripts: PrescriptionDto[]): PrescriptionDtoExt[] 
	{
		const scriptItems = this.setScriptRepeatStatuses(scripts);

		if (!scriptItems || scriptItems.length === 0) {
			return [];
		}
		return scriptItems.filter(s =>
			s.state === 'Active' &&
			(s.repeatStatus === 'Available' || s.repeatStatus === 'Order now')
		);
	}

	public setScriptRepeatStatuses(scripts: PrescriptionDto[]): PrescriptionDtoExt[]
	{
		if (!scripts || scripts.length === 0)
		{
			return [];
		}
		//make it a new array with PrescriptionDtoExt
		const extendedScripts: PrescriptionDtoExt[] = scripts.map(script => ({ ...script, repeatStatus: null }));

		extendedScripts.forEach(script =>
		{
			const now = new Date();
			const hasDueDates = script.repeatDueDates && script.repeatDueDates.length > 0;
			const nextDueDate = hasDueDates ? new Date(script.repeatDueDates[0]) : null;

			if (script.state === 'Active' && script.repeatSaleOrderStatus === 'OnHold')
			{
				script.repeatStatus = 'Unpaid';
			}
			else if (script.state === 'Active' && script.repeatSaleOrderStatus === 'New')
			{
				script.repeatStatus = 'Processing';
			}
			else if (script.state === 'Active' &&
				script.repeatDueDates && script.repeatDueDates.some(dueDateStr =>
				{
					const dueDate = new Date(dueDateStr);
					return dueDate <= now;
				}) &&
				(!script.repeatSaleOrderStatus || (script.repeatSaleOrderStatus == 'Cancelled' || script.repeatSaleOrderStatus == 'Abandoned' || script.repeatSaleOrderStatus == 'Completed')))
			{
				script.repeatStatus = 'Order now';
			}
			else if (script.state === 'Active' &&
				script.repeatDueDates && script.repeatDueDates.some(dueDateStr =>
				{
					const dueDate = new Date(dueDateStr);
					return dueDate > now;
				}) &&
				(!script.repeatSaleOrderStatus || (script.repeatSaleOrderStatus == 'Cancelled' || script.repeatSaleOrderStatus == 'Abandoned' || script.repeatSaleOrderStatus == 'Completed')))
			{
				script.repeatStatus = 'Available';
			}
			else if (script.state === PrescriptionState.Expired)
			{
				script.repeatStatus = 'Expired';
			}
			else if (script.state === PrescriptionState.Complete)
			{
				script.repeatStatus = 'Complete';
			}
			else
			{
				script.repeatStatus = null;
			}
		});
		return extendedScripts;
	}

	/*
	 * Filters scripts to get those that are Active and have an active repeat order
	 */
	public getScriptsActiveOrders(scripts: PrescriptionDto[]): PrescriptionDto[]
	{
		if (!scripts || scripts.length === 0)
		{
			return [];
		}

		const now = new Date();

		return scripts.filter(script =>
			script.state === 'Active' && script.repeatSaleOrderStatus && script.repeatSaleOrderStatus != 'Cancelled' && script.repeatSaleOrderStatus != 'Abandoned' && script.repeatSaleOrderStatus != 'Completed'
		);
	}

	/**
	 * Filters scripts to get those that are Active and have repeats due in the future
	 */
	public getScriptsOtherRepeats(scripts: PrescriptionDto[]): PrescriptionDto[]
	{
		if (!scripts || scripts.length === 0)
		{
			return [];
		}

		const now = new Date();

		return scripts.filter(script =>
			script.state === 'Active' &&
			(script.repeatDueDates && script.repeatDueDates[0] && new Date(script.repeatDueDates[0]) > now) &&
			!script.repeatSaleOrderStatus
		);
	}

	public sendRepeatOrderToPharmacy(scripts: PrescriptionDto[], profile: PharmacyProfile, deliveryType: PrescriptionOrderDeliveryType): Observable<SaleOrderDto>
	{
		const prescriptionOrder: CreatePrescriptionOrderDto = {
			scriptIds: scripts.map(s => s.id!),
			pharmacyLinkId: profile.pharmacyLink.id!,
			deliveryType: deliveryType,
		};

		console.debug('Sending script order to prescription service:', prescriptionOrder);

		// Create the sales order
		return this.prescriptionService.createSaleOrder(prescriptionOrder, {});
	}

	public deleteSaleOrder(repeatSaleOrderId: string): Observable<SaleOrderDto>
	{
		return this.prescriptionService.deleteSaleOrder(repeatSaleOrderId, {});
	}

	public getSaleOrder(saleOrderId: string): Observable<SaleOrderDto>
	{
		return this.prescriptionService.getSaleOrder(saleOrderId, {});
	}

	public async disconnectFromCurrentPharmacy(pharmacyLinkId: any, redirect: boolean = true): Promise<void>
	{
		//check pharmacyLinkId
		if (!pharmacyLinkId)
		{
			throw new Error('Pharmacy link ID is required to disconnect');
		}

		//check it matches current profile
		const currentProfile = this.currentPharmacyProfile$.getValue();
		if (!currentProfile || currentProfile.pharmacyLink.id !== pharmacyLinkId)
		{
			throw new Error('Pharmacy link ID does not match current profile');
		}

		this.setCurrentPharmacyProfile(null, false);

		await firstValueFrom(this.pharmacyLinkService.delete(pharmacyLinkId));
	}
}

export interface PrescriptionDtoExt extends PrescriptionDto
{
	repeatStatus: 'Processing' | 'Order now' | 'Available' | 'Unpaid' | 'Expired' | 'Complete' | null;
}